import type { HoleFlag } from '../api/types';

type Props = {
  pars: number[];
  flags?: HoleFlag[];
  onChange: (pars: number[]) => void;
  onFlagsChange?: (flags: HoleFlag[]) => void;
  showFlags?: boolean;
};

export function ParGrid({ pars, flags, onChange, onFlagsChange, showFlags = false }: Props) {
  const setPar = (i: number, v: number) => {
    const next = [...pars];
    next[i] = v;
    onChange(next);
  };

  const getFlag = (holeNumber: number): { isKp: boolean; isLd: boolean; isHio: boolean } => {
    const f = flags?.find((x) => x.holeNumber === holeNumber);
    return { isKp: f?.isKp ?? false, isLd: f?.isLd ?? false, isHio: f?.isHio ?? false };
  };

  const toggle = (holeNumber: number, key: 'isKp' | 'isLd' | 'isHio') => {
    if (!onFlagsChange) return;
    const base = pars.map((_, i) => {
      const h = i + 1;
      const cur = getFlag(h);
      return { holeNumber: h, isKp: cur.isKp, isLd: cur.isLd, isHio: cur.isHio };
    });
    const idx = base.findIndex((b) => b.holeNumber === holeNumber);
    if (idx < 0) return;
    const cur = base[idx]!;
    const willBe = !cur[key];

    if (key === 'isHio' && willBe) {
      // Only one HIO hole per event — clear HIO on every other row.
      for (let i = 0; i < base.length; i++) {
        if (i !== idx) base[i] = { ...base[i]!, isHio: false };
      }
    }

    base[idx] = {
      holeNumber,
      isKp: key === 'isKp' ? willBe : willBe ? false : cur.isKp,
      isLd: key === 'isLd' ? willBe : willBe ? false : cur.isLd,
      isHio: key === 'isHio' ? willBe : willBe ? false : cur.isHio,
    };
    onFlagsChange(base);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(86px, 1fr))', gap: 8 }}>
      {pars.map((p, i) => {
        const holeNumber = i + 1;
        const flag = getFlag(holeNumber);
        return (
          <div key={i} style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Hole {holeNumber}</span>
            <select
              value={p}
              onChange={(e) => setPar(i, Number(e.target.value))}
              style={{ padding: 6, textAlign: 'center' }}
              aria-label={`Par for hole ${holeNumber}`}
            >
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
            </select>
            {showFlags && onFlagsChange && (
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={`flag-chip ${flag.isKp ? 'flag-chip-kp' : 'flag-chip-off'}`}
                  onClick={() => toggle(holeNumber, 'isKp')}
                  aria-pressed={flag.isKp}
                  aria-label={`Toggle Closest to Pin on hole ${holeNumber}`}
                >
                  KP
                </button>
                <button
                  type="button"
                  className={`flag-chip ${flag.isLd ? 'flag-chip-ld' : 'flag-chip-off'}`}
                  onClick={() => toggle(holeNumber, 'isLd')}
                  aria-pressed={flag.isLd}
                  aria-label={`Toggle Longest Drive on hole ${holeNumber}`}
                >
                  LD
                </button>
                <button
                  type="button"
                  className={`flag-chip ${flag.isHio ? 'flag-chip-hio' : 'flag-chip-off'}`}
                  onClick={() => toggle(holeNumber, 'isHio')}
                  aria-pressed={flag.isHio}
                  aria-label={`Toggle Hole-in-One prize hole on hole ${holeNumber}`}
                >
                  HIO
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
